import { toNum, toNumIfVal } from "@agrigate/ts-utils/lib/coercions";
import { crunchMap } from "@agrigate/ts-utils/lib/collections";
import { thruUnlessN } from "@agrigate/ts-utils/lib/nullability";
import { isPrimitive } from "@agrigate/ts-utils/lib/validations";
import { formatDistanceToNowStrict } from "date-fns";
import { isNil, startCase, upperFirst } from "lodash";
import { convertKgToClosestUnit, getQuantityTypeShortForm } from "./quantity";
export const formatName = (name) => {
    if (!name)
        return name;
    return name.split(/\s+/).map(upperFirst).join(" ");
};
export const formatDecimal = (amount) => {
    if (amount == null)
        return amount;
    if (typeof amount === "string")
        return amount;
    return amount.toFixed(2);
};
export const inferSettlementUnit = (row) => {
    if (row?.numBatches || row?.numExpectedBatches || row?.amountPerBatch) {
        if (row?.batchSize === 12) {
            return "DOZEN";
        }
        else if (row?.batchSize === 1) {
            return "ITEM";
        }
        else {
            throw new Error(`Missing or unexpected batch size: ${row?.batchSize}`);
        }
    }
    return "KG";
};
export const formatBatches = (row) => {
    if (row?.batchSize === 12)
        return "dozen";
    else if (row?.batchSize === 1)
        return "item";
    else
        return "batches";
};
export const formatQuantity = (row, defaultVal = "N/A") => {
    if (row?.numBatches)
        return `${formatQuantityDigits(row.numBatches)} ${getQuantityTypeShortForm(formatBatches(row))}`;
    if (row?.weightKg) {
        const { inputValue, inputUnit } = convertKgToClosestUnit(toNum(row.weightKg));
        const shortForm = getQuantityTypeShortForm(inputUnit) || inputUnit;
        return `${inputValue} ${shortForm}`;
    }
    return defaultVal;
};
export const formatQuantityDiff = (left, right) => {
    if (right?.numBatches != null)
        return `${left?.numBatches ?? 0} / ${right.numBatches} ${getQuantityTypeShortForm(formatBatches(right))}`;
    if (right?.weightKg != null) {
        const { inputValue: rInputValue, inputUnit: rInputUnit } = convertKgToClosestUnit(toNum(right.weightKg));
        const { inputValue: lInputValue, inputUnit: lInputUnit } = convertKgToClosestUnit(toNum(left?.weightKg ?? 0));
        const lShortForm = getQuantityTypeShortForm(lInputUnit) || lInputUnit;
        const rShortForm = getQuantityTypeShortForm(rInputUnit) || rInputUnit;
        return `${lInputValue} ${lShortForm} / ${rInputValue} ${rShortForm}`;
    }
    return "";
};
export const formatExpectedQuantity = (row, defaultVal = "N/A") => {
    if (row?.numExpectedBatches)
        return `${row.numExpectedBatches} ${getQuantityTypeShortForm(formatBatches(row))}`;
    if (row?.expectedWeightKg) {
        const { inputValue, inputUnit } = convertKgToClosestUnit(toNum(row.expectedWeightKg));
        const shortForm = getQuantityTypeShortForm(inputUnit) || inputUnit;
        return `${inputValue} ${shortForm}`;
    }
    return defaultVal;
};
export const formatRate = (row) => {
    if (row?.amountPerBatch)
        return `${formatToIndianCurrency(row.amountPerBatch)}/${getQuantityTypeShortForm(formatBatches(row))}`;
    if (row?.amountPerKg)
        return `${formatMoney(row.amountPerKg)}/kg`;
    return "N/A";
};
export const formatAddr = (addr) => {
    if (!addr)
        return null;
    return [
        addr.areaDetails?.address,
        thruUnlessN(addr.areaDetails?.buildingNumber, _ => `Building No. ${_}`),
        thruUnlessN(addr.areaDetails?.streetName, _ => `Street ${_}`),
        thruUnlessN(addr.areaDetails?.tehsil, _ => `Tehsil ${_}`),
        thruUnlessN(addr.areaDetails?.ward, _ => `Ward ${_}`),
        thruUnlessN(addr.areaDetails?.block, _ => `Block ${_}`),
        thruUnlessN(addr.areaDetails?.cluster, _ => `Cluster ${_}`),
        thruUnlessN(addr.areaDetails?.postOffice, _ => `PO ${_}`),
        addr.cityOrVillage,
        addr.district,
        addr.state,
        addr.pinCode,
    ]
        .filter(Boolean)
        .map(_ => _?.trim())
        .join(", ");
};
export const formatPTFieldVal = (inputVal) => {
    // biome-ignore lint/suspicious/noExplicitAny: Required for dynamic type handling
    const val = inputVal;
    if (isNil(val))
        return "-";
    if (isPrimitive(val))
        return `${val}`;
    if (val.unit) {
        const unitVal = val;
        return `${unitVal.input} ${unitVal.unit.toLowerCase()}`;
    }
    if (val.start || val.end) {
        const rangeVal = val;
        if (!isNil(rangeVal.start) && !isNil(rangeVal.end))
            return `${rangeVal.start}-${rangeVal.end}`;
        if (isNil(rangeVal.start) && isNil(rangeVal.end))
            return "-";
        if (isNil(rangeVal.start))
            return `< ${rangeVal.end}`;
        if (isNil(rangeVal.end))
            return `> ${rangeVal.start}`;
    }
    return "-";
};
export function formatToIndianCurrency(val, addSpaceAfterSymbol = false, disableCurrencySymbol) {
    const amount = toNumIfVal(val);
    if (amount == null)
        return "";
    let formatted = new Intl.NumberFormat("en-IN", {
        style: disableCurrencySymbol ? undefined : "currency",
        currency: disableCurrencySymbol ? undefined : "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
    if (addSpaceAfterSymbol) {
        formatted = formatted.replace("₹", "₹ ");
    }
    return formatted;
}
export const formatMoney = (val, disableCurrencySymbol) => {
    const amount = toNumIfVal(val);
    if (amount == null)
        return "";
    return Intl.NumberFormat("en-IN", {
        style: disableCurrencySymbol ? undefined : "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount);
};
export const formatQuantityDigits = (val) => {
    const quanity = Number(val ?? "");
    const hasDecimals = quanity % 1 !== 0;
    if (val == null)
        return "";
    return Intl.NumberFormat("en-IN", {
        style: undefined,
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
    }).format(val);
};
export const formatPercent = (val) => {
    const numVal = toNumIfVal(val);
    if (numVal == null)
        return "";
    return `${numVal.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")} %`;
};
export const formatMoneyHumanFriendly = (val) => {
    const numVal = toNumIfVal(val);
    if (numVal == null)
        return "";
    if (numVal > 10000000)
        return `${formatMoney((numVal / 10000000).toFixed(1))} Crore`;
    if (numVal > 10000)
        return `${formatMoney((numVal / 100000).toFixed(1))} Lakhs`;
    if (numVal > 1000)
        return `${formatMoney((numVal / 1000).toFixed(1))} Thousand`;
    return formatMoney(val);
};
export const formatScalar = (val, _disableCurrencySymbol) => {
    if (val == null)
        return "";
    return Intl.NumberFormat("en-IN").format(val); // The API works fine with string, but ts defs only mention number
};
export const formatFields = (props, fields) => crunchMap(Object.entries(props), ([name, value]) => {
    const field = fields.find(_ => _.name === name);
    const label = field?.label ?? startCase(name);
    if (isPrimitive(value)) {
        return {
            label,
            value,
        };
    }
    let rangeStr = null;
    if (value.min && value.max)
        rangeStr = `${value.min}-${value.max}`;
    else if (value.min)
        rangeStr = `>= ${value.min}`;
    else if (value.max)
        rangeStr = `<= ${value.max}`;
    if (!rangeStr)
        return null;
    return {
        label,
        value: rangeStr,
    };
});
export const formatRange = (range) => {
    if (!range)
        return "-";
    if (range.min && range.max)
        return `${range.min}-${range.max}`;
    else if (range.min)
        return `>= ${range.min}`;
    else if (range.max)
        return `<= ${range.max}`;
    return "-";
};
export const customTimeAgo = (date) => {
    const distance = formatDistanceToNowStrict(new Date(date), {
        roundingMethod: "floor",
    });
    return distance
        .replace(" hours", "hr")
        .replace(" hour", "hr")
        .replace(" minutes", "min")
        .replace(" minute", "min")
        .replace(" seconds", "sec")
        .replace(" second", "sec")
        .concat(" ago");
};
export const formatId = (row) => {
    if (row?.pubId)
        return row.pubId;
    if (row?.id)
        return `#${row.id}`;
    return "N/A";
};
export const formatStatus = (status) => {
    return status.replace(/_/g, " ");
};
//# sourceMappingURL=formatters.js.map